<template>
  <div class="home-charts-grid">
    <!-- Chart 1: Traffic & User Trend (Area Chart) -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-left">
          <i class="fas fa-chart-area header-icon icon-blue"></i>
          <div>
            <h3 class="chart-title">Xu Hướng Truy Cập & Người Dùng</h3>
            <p class="chart-subtitle">
              Số lượt tương tác (Visits) và người dùng hoạt động (Users) trong {{ rangeLabel }}
            </p>
          </div>
        </div>
      </div>
      <div class="card-body">
        <apexchart
          v-if="hasTrafficData"
          type="area"
          height="290"
          :options="trafficOptions"
          :series="trafficSeries"
        ></apexchart>
        <el-empty v-else description="Chưa có dữ liệu lượt truy cập"></el-empty>
      </div>
    </div>

    <!-- Chart 2: Content Growth (Clustered Bar Chart) -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-left">
          <i class="fas fa-layer-group header-icon icon-purple"></i>
          <div>
            <h3 class="chart-title">Tăng Trưởng Dữ Liệu Hệ Thống</h3>
            <p class="chart-subtitle">
              Quy mô tích lũy điểm đến dịch vụ, tour sự kiện và bài viết 6 tháng qua
            </p>
          </div>
        </div>
      </div>
      <div class="card-body">
        <apexchart
          v-if="hasContentGrowthData"
          type="bar"
          height="290"
          :options="contentGrowthOptions"
          :series="contentGrowthSeries"
        ></apexchart>
        <el-empty v-else description="Chưa có dữ liệu tăng trưởng nội dung"></el-empty>
      </div>
    </div>

    <!-- Chart 3: Module Breakdown (Donut Chart) -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-left">
          <i class="fas fa-chart-pie header-icon icon-amber"></i>
          <div>
            <h3 class="chart-title">Cơ Cấu Số Liệu Các Module</h3>
            <p class="chart-subtitle">
              Tỷ trọng Danh thắng, Lưu trú, Sự kiện, Giải trí, Ăn uống, Tour, Doanh nghiệp, HDV & Blog
            </p>
          </div>
        </div>
      </div>
      <div class="card-body">
        <apexchart
          v-if="hasDonutData"
          type="donut"
          height="290"
          :options="donutOptions"
          :series="donutSeries"
        ></apexchart>
        <el-empty v-else description="Chưa có dữ liệu thống kê module"></el-empty>
      </div>
    </div>

    <!-- Chart 4: Country Distribution (Bar Chart) -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-left">
          <i class="fas fa-globe-asia header-icon icon-teal"></i>
          <div>
            <h3 class="chart-title">Phân Bố Khách Theo Quốc Gia</h3>
            <p class="chart-subtitle">
              Tỷ trọng khách du lịch trong nước và quốc tế truy cập trong {{ rangeLabel }}
            </p>
          </div>
        </div>
      </div>
      <div class="card-body">
        <apexchart
          v-if="hasCountryData"
          type="bar"
          height="290"
          :options="countryOptions"
          :series="countrySeries"
        ></apexchart>
        <el-empty v-else description="Chưa có dữ liệu quốc gia"></el-empty>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'HomeChartsGrid',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    trafficTrend: {
      type: Array,
      default: () => []
    },
    contentGrowth: {
      type: Array,
      default: () => []
    },
    countryShare: {
      type: Array,
      default: () => []
    },
    categoriesData: {
      type: Array,
      default: () => []
    },
    rangeLabel: {
      type: String,
      default: '30 phút qua'
    }
  },
  computed: {
    // 1. Traffic Trend
    hasTrafficData() {
      return this.trafficTrend && this.trafficTrend.length > 0
    },
    trafficSeries() {
      return [
        {
          name: 'Lượt tương tác',
          data: this.trafficTrend.map((t) => t.visits || 0)
        },
        {
          name: 'Người dùng',
          data: this.trafficTrend.map((t) => t.users || 0)
        }
      ]
    },
    trafficOptions() {
      const categories = this.trafficTrend.map((t) => t.date || '')
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        colors: ['#3b82f6', '#10b981'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [0, 95, 100]
          }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        xaxis: {
          categories,
          labels: {
            style: { fontSize: '11px', colors: '#64748b' }
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px', colors: '#64748b' }
          }
        },
        grid: { borderColor: '#f1f5f9' },
        tooltip: {
          shared: true,
          theme: 'light'
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          fontSize: '12px',
          fontWeight: 600
        }
      }
    },

    // 2. Content Growth
    hasContentGrowthData() {
      return this.contentGrowth && this.contentGrowth.length > 0
    },
    contentGrowthSeries() {
      return [
        {
          name: 'Điểm đến & Dịch vụ',
          data: this.contentGrowth.map((c) => c.locations || 0)
        },
        {
          name: 'Bài viết / Blog',
          data: this.contentGrowth.map((c) => c.blogs || 0)
        },
        {
          name: 'Tour & Sự kiện',
          data: this.contentGrowth.map((c) => c.pages || 0)
        }
      ]
    },
    contentGrowthOptions() {
      const categories = this.contentGrowth.map((c) => c.month || '')
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        colors: ['#8b5cf6', '#0284c7', '#10b981'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 4
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: {
            style: { fontSize: '11px', colors: '#64748b' }
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px', colors: '#64748b' }
          }
        },
        grid: { borderColor: '#f1f5f9' },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
          fontSize: '12px',
          fontWeight: 600
        }
      }
    },

    // 3. Module Breakdown Donut
    hasDonutData() {
      return this.donutSeries && this.donutSeries.length > 0
    },
    donutSeries() {
      if (this.categoriesData && this.categoriesData.length > 0) {
        return this.categoriesData.map((c) => c.count || 0)
      }
      return [15, 12, 8, 10, 18, 9, 14, 7, 25]
    },
    donutLabels() {
      if (this.categoriesData && this.categoriesData.length > 0) {
        return this.categoriesData.map((c) => c.name || '')
      }
      return [
        'Danh thắng',
        'Lưu trú',
        'Sự kiện',
        'Giải trí',
        'Ăn uống',
        'Tour',
        'Doanh nghiệp',
        'Hướng dẫn viên',
        'Bài viết Blog'
      ]
    },
    donutOptions() {
      return {
        chart: {
          type: 'donut',
          fontFamily: 'inherit'
        },
        labels: this.donutLabels,
        colors: [
          '#0284c7',
          '#4f46e5',
          '#f59e0b',
          '#ec4899',
          '#ea580c',
          '#0d9488',
          '#7c3aed',
          '#0891b2',
          '#10b981'
        ],
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                name: {
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#64748b'
                },
                value: {
                  fontSize: '24px',
                  fontWeight: 800,
                  color: '#1e293b'
                },
                total: {
                  show: true,
                  label: 'Tổng dữ liệu',
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600
                }
              }
            }
          }
        },
        dataLabels: { enabled: false },
        legend: {
          position: 'bottom',
          fontSize: '12px',
          fontWeight: 600,
          markers: { radius: 3 }
        }
      }
    },

    // 4. Country Distribution
    hasCountryData() {
      return this.countryShare && this.countryShare.length > 0
    },
    countrySeries() {
      return [
        {
          name: 'Lượt người dùng',
          data: this.countryShare.map((c) => c.count || 0)
        }
      ]
    },
    countryOptions() {
      const categories = this.countryShare.map((c) => c.country || 'Khác')
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        colors: ['#0d9488'],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: '55%'
          }
        },
        dataLabels: {
          enabled: true,
          formatter(val) {
            return val
          },
          style: {
            fontSize: '11px',
            fontWeight: 700,
            colors: ['#1e293b']
          },
          offsetX: 10
        },
        xaxis: {
          categories,
          labels: {
            style: { fontSize: '11px', colors: '#64748b' }
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: '12px', fontWeight: 600, colors: '#334155' }
          }
        },
        grid: { borderColor: '#f1f5f9' },
        tooltip: {
          y: {
            formatter(val) {
              return `${val} người dùng`
            }
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.home-charts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  .chart-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .title-left {
        display: flex;
        align-items: center;
        gap: 10px;

        .header-icon {
          font-size: 18px;

          &.icon-blue { color: #3b82f6; }
          &.icon-purple { color: #8b5cf6; }
          &.icon-amber { color: #f59e0b; }
          &.icon-teal { color: #0d9488; }
        }

        .chart-title {
          margin: 0;
          font-size: 15px;
          font-weight: 800;
          color: #1e293b;
        }

        .chart-subtitle {
          margin: 3px 0 0 0;
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }
      }
    }

    .card-body {
      min-height: 290px;
      display: flex;
      align-items: center;
      justify-content: center;

      ::v-deep .apexcharts-canvas {
        width: 100% !important;
      }
    }
  }
}
</style>
